/**
 * This component contains code to edit an existing software's details.
 * when Clinical is selected in machine use type software part-id and software configuration are optional.
 * when Non-Clinical is selected in machine use type software part-id and software configuration are mandatory.
 */
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CardHeader,
  IconButton,
  Grid,
  TextField,
  InputLabel,
  MenuItem,
  Checkbox,
  ListItemText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashedDivider from "common/ui/DashedDivider";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "context/hooks";
import { setEditSoftware } from "context/modal.context";
import { selectEditSoftware } from "context/modal.selector";
import { Controller } from "react-hook-form";
import useForm from "hooks/useForm";
import yup from "helpers/validator";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useMachineHardwareVersion,
  useMachineUses
} from "services/machines.service";
import { toast } from "react-hot-toast";
import MachineUse from "types/MachineType.enum";
import { useQueryClient } from "@tanstack/react-query";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import { useUpdateSoftware } from "services/softwares.service";
import { decode, encode } from "utils/common";

const defaultValues = {
  softwarePackageTypeId: "",
  machineUseId: [],
  softwarePackageName: "",
  softwareVersion: "",
  softwarePartId: "",
  softwareConfiguration: "",
  hardwareVersionId: [],
  description: ""
};

function EditSoftware() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectEditSoftware);
  const machineUses = useMachineUses();
  const machineHardwareVersion = useMachineHardwareVersion();
  const queryClient = useQueryClient();

  const editSoftwareFormSchema = yup.object().shape({
    softwarePackageTypeId: yup.string(),
    machineUseId: yup.array().min(1, t("editSoftware.machineUseRequired")),
    softwarePackageName: yup
      .string()
      .transform((val) => val?.trim())
      .required(t("editSoftware.packageNameRequired")),
    softwareVersion: yup
      .string()
      .transform((val) => val?.trim())
      .required(t("editSoftware.versionRequired")),
    softwarePartId: yup
      .string()
      .transform((val) => val?.trim())
      .max(100, t("editSoftware.softwarePartIdLimit"))
      .when("machineUseId", ([machineUseIds], schema) => {
        if (machineUseIds.some((id) => id === MachineUse.clinical)) {
          return schema.required(t("editSoftware.partIdRequired"));
        }
      }),
    softwareConfiguration: yup
      .string()
      .transform((val) => val?.trim())
      .max(100, t("editSoftware.softwareConfigurationLimit"))
      .when("machineUseId", ([machineUseIds], schema) => {
        if (machineUseIds.some((id) => id === MachineUse.clinical)) {
          return schema.required(t("editSoftware.configRequired"));
        }
      }),
    hardwareVersionId: yup
      .array()
      .min(1, t("editSoftware.hardwareVersionRequired")),
    description: yup
      .string()
      .transform((val) => val?.trim())
      .max(500, t("editSoftware.softwareDescriptionLimit"))
      .transform((val) => encode(val))
  });

  const {
    register,
    reset,
    handleSubmit,
    watch,
    clearErrors,
    formState: { errors },
    control
  } = useForm({
    defaultValues,
    resolver: yupResolver(editSoftwareFormSchema)
  });

  React.useEffect(() => {
    if (props.open) {
      reset({
        softwarePackageTypeId: props.software?.softwarePackageCategory.name,
        machineUseId: props.software?.machineUse.map(
          ({ machineUseId }) => machineUseId
        ),
        softwarePackageName: props.software?.name ?? "",
        softwareVersion: props.software?.softwareVersion ?? "",
        softwarePartId: props.software?.softwarePartId ?? "",
        softwareConfiguration: props.software?.softwareConfiguration ?? "",
        hardwareVersionId: props.software?.hardwareVersions.map(
          ({ hardwareVersionId }) => hardwareVersionId
        ),
        description: decode(props.software?.description ?? "")
      });
    }
  }, [props.open]);

  const machineUse = watch("machineUseId");
  const isClinicalSoftware = machineUse.some(
    (id) => id === MachineUse.clinical
  );

  const updateSoftware = useUpdateSoftware(onSoftwareUpdated);

  function onSoftwareUpdated(_data, error) {
    queryClient.invalidateQueries({
      queryKey: ["software", "list"],
      exact: false
    });

    if (!error) {
      toast.success(t("editSoftware.softwareUpdatedSuccessfully"));
    }

    dismiss();
  }

  function dismiss() {
    clearErrors();
    dispatch(
      setEditSoftware({
        open: false,
        software: null
      })
    );
  }

  function onSubmit(data) {
    const formData = {
      machineUses: data.machineUseId.map((machineUseId) => {
        const machineUse = props.software?.machineUse.find(
          (m) => m.machineUseId === machineUseId
        );
        return { id: machineUse?.id ?? 0, machineUseId };
      }),
      softwareVersion: data.softwareVersion,
      softwarePartId: data.softwarePartId,
      softwareConfiguration: data.softwareConfiguration,
      hardwareVersions: data.hardwareVersionId.map((hardwareVersionId) => {
        const hardwareVersion = props.software?.hardwareVersions.find(
          (h) => h.hardwareVersionId === hardwareVersionId
        );
        return { id: hardwareVersion?.id ?? 0, hardwareVersionId };
      }),
      description: data.description
    };

    updateSoftware.mutate({ softwareId: props.software?.id, ...formData });
  }

  return (
    <Dialog open={props.open} maxWidth="sm" fullWidth scroll="body">
      <DialogTitle
        component={CardHeader}
        avatar={<EditIcon color="primary" />}
        title={t("editSoftware.editSoftware")}
        titleTypographyProps={{
          variant: "h6",
          fontWeight: "medium",
          color: "primary.main"
        }}
        action={
          <IconButton onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        }
      />
      <DashedDivider style={{ width: "93%" }} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <InputLabel htmlFor="softwarePackageTypeId" required>
                {t("editSoftware.softwarePackageType")}
              </InputLabel>
              <TextField
                id="softwarePackageTypeId"
                {...register("softwarePackageTypeId")}
                type="text"
                fullWidth
                disabled
                size="small"
                error={!!errors.softwarePackageTypeId}
                helperText={errors.softwarePackageTypeId?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel htmlFor="machineUseId" required>
                {t("editSoftware.machineUseType")}
              </InputLabel>
              <Controller
                control={control}
                name="machineUseId"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextField
                      id="machineUseId"
                      fullWidth
                      type="text"
                      size="small"
                      value={value}
                      onChange={(event) => {
                        const value = event.target.value;
                        onChange(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      error={!!errors.machineUseId}
                      helperText={errors.machineUseId?.message}
                      select
                      SelectProps={{
                        displayEmpty: true,
                        multiple: true,
                        renderValue: (selected: string[]) => {
                          if (selected.length === 0) {
                            return t("common.select");
                          }

                          return selected
                            .map(
                              (id) =>
                                machineUses.data.find((use) => use.id === id)
                                  ?.name
                            )
                            .join(", ");
                        }
                      }}
                    >
                      {machineUses.data.map((machineUse) => (
                        <MenuItem
                          disableGutters
                          key={machineUse.id}
                          value={machineUse.id}
                        >
                          <Checkbox
                            size="small"
                            checked={value.indexOf(machineUse.id) > -1}
                          />
                          <ListItemText primary={machineUse.name} />
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel htmlFor="softwarePackageName" required>
                {t("editSoftware.softwarePackageName")}
              </InputLabel>
              <TextField
                id="softwarePackageName"
                {...register("softwarePackageName")}
                type="text"
                fullWidth
                disabled
                size="small"
                error={!!errors.softwarePackageName}
                helperText={errors.softwarePackageName?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel htmlFor="softwareVersion" required>
                {t("editSoftware.softwareVersion")}
              </InputLabel>
              <TextField
                id="softwareVersion"
                {...register("softwareVersion")}
                type="text"
                fullWidth
                size="small"
                error={!!errors.softwareVersion}
                helperText={errors.softwareVersion?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel
                htmlFor="softwarePartId"
                required={isClinicalSoftware}
              >
                {t("editSoftware.softwarePartID")}
              </InputLabel>
              <TextField
                id="softwarePartId"
                {...register("softwarePartId")}
                type="text"
                fullWidth
                size="small"
                error={!!errors.softwarePartId}
                helperText={errors.softwarePartId?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel
                htmlFor="softwareConfiguration"
                required={isClinicalSoftware}
              >
                {t("editSoftware.softwareConfiguration")}
              </InputLabel>
              <TextField
                id="softwareConfiguration"
                {...register("softwareConfiguration")}
                type="text"
                fullWidth
                size="small"
                error={!!errors.softwareConfiguration}
                helperText={errors.softwareConfiguration?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputLabel htmlFor="hardwareVersionId" required>
                {t("editSoftware.hardwareVersions")}
              </InputLabel>
              <Controller
                control={control}
                name="hardwareVersionId"
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextField
                      id="hardwareVersionId"
                      type="text"
                      fullWidth
                      size="small"
                      value={value}
                      onChange={(event) => {
                        const value = event.target.value;
                        onChange(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      error={!!errors.hardwareVersionId}
                      helperText={errors.hardwareVersionId?.message}
                      select
                      SelectProps={{
                        displayEmpty: true,
                        multiple: true,
                        renderValue: (selected: string[]) => {
                          if (selected.length === 0) {
                            return t("common.select");
                          }

                          return selected
                            .map(
                              (id) =>
                                machineHardwareVersion.data.find(
                                  (hardware) => hardware.id === id
                                )?.name
                            )
                            .join(", ");
                        }
                      }}
                    >
                      {machineHardwareVersion.data.map((hardware) => (
                        <MenuItem
                          disableGutters
                          key={hardware.id}
                          value={hardware.id}
                        >
                          <Checkbox
                            size="small"
                            checked={value.indexOf(hardware.id) > -1}
                          />
                          <ListItemText primary={hardware.name} />
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor="description">
                {t("editSoftware.softwareDescription")}
              </InputLabel>
              <TextField
                id="description"
                {...register("description")}
                type="text"
                multiline
                rows={4}
                fullWidth
                size="small"
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DashedDivider style={{ width: "93%" }} />
        <DialogActions sx={{ justifyContent: "center", padding: 2 }}>
          <Button variant="outlined" color="gray" onClick={dismiss}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="outlined" startIcon={<DoneIcon />}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditSoftware;
